import type {
  CircleLayerSpecification,
  FillExtrusionLayerSpecification,
  FillLayerSpecification,
  GeoJSONSourceSpecification,
  LineLayerSpecification,
} from 'maplibre-gl'
import type { Feature, FeatureCollection, Point, Polygon, LineString } from 'geojson'
import type { Geofence, TrafficObservation, Vessel, TwinOverlay } from '../../types'

/* ============================================================================
 * ПОДГОТОВЛЕННЫЕ ОПРЕДЕЛЕНИЯ СЛОЁВ КАРТЫ
 *
 * Слои консоли ИВО:
 *   1. missions              — районы выполнения заданий
 *   2. geofences             — геозоны (запрет, ограничение, зона контроля)
 *   3. declarations          — декларации о полётах с маршрутами
 *   4. cooperativeTracks     — кооперативные цели: ADS-B / Remote ID (ЭРА-ГЛОНАСС)
 *   5. nonCooperativeTracks  — некооперативные цели: РЛС / RF-пеленгование
 *   6. intruders             — НАРУШИТЕЛИ, красные, пульсируют
 *
 * Формат обмена данными — ГОСТ Р 71934-2025. Координаты WGS-84 [lon, lat].
 * ========================================================================= */

/** Идентификаторы источников данных */
export const SRC = {
  missions: 'src-missions',
  geofences: 'src-geofences',
  declarations: 'src-declarations',
  cooperative: 'src-coop',
  nonCooperative: 'src-noncoop',
  intruders: 'src-intruders',
  vessels: 'src-vessels',
  /** Хвосты траекторий (FR24-стиль) — 2026-10-07 */
  trails: 'src-trails',
  /** Прогнозные дуги: 10с (яркая) и 60с (пунктир) */
  predict10: 'src-predict10',
  predict60: 'src-predict60',
  /** Оверлей двойника (D3): reality-хвосты бортов */
  overlayReal: 'src-overlay-real',
  /** Оверлей двойника (D3): twin-прогноз бортов (пунктир) */
  overlayTwin: 'src-overlay-twin',
} as const

/** Цвета слоёв (совпадают с design-tokens.json → map.layers) */
export const LAYER_COLOR = {
  missions: '#38bdf8',
  geofences: '#a78bfa',
  declarations: '#f59e0b',
  cooperative: '#4ade80',
  nonCooperative: '#f59e0b',
  intruders: '#ef4444',
  vessels: '#60a5fa',
  trails: '#4ade80',
  predict: '#fbbf24',
  /** Дуга прогноза борта-участника предсказанного конфликта (miss < 500 м) */
  predictConflict: '#ef4444',
  /** Оверлей двойника: reality-хвост (факт) — бирюзовый */
  overlayReal: '#22d3ee',
  /** Оверлей двойника: twin-прогноз — малиновый пунктир (двойник) */
  overlayTwin: '#f472b6',
  // Цвета по среде. air — небо (голубой), water — море (бирюзовый),
  // land — суша (охристый). Гибриды различаются штриховкой в data-выражении.
  envAir: '#38bdf8',
  envWater: '#22d3ee',
  envLand: '#d97706',
  envHybrid: '#a78bfa',
} as const

/** Пустой GeoJSON-коллектор для инициализации источника */
const emptyCollection = (): FeatureCollection => ({ type: 'FeatureCollection', features: [] })

/* ============================================================================
 * 3D-СЛОИ: ОБЪЁМНЫЕ ГЕОЗОНЫ И РАЙОНЫ (задача №14)
 *
 * БЫЛО: геозоны и районы рисовались плоскими fill + контур. Поля
 * upper_limit/lower_limit из /api/geo при этом ТЕРЯЛИСЬ при преобразовании в
 * GeoJSON — высота зоны была в ответе бэкенда, но не доходила до карты.
 * Задача конкурента «Цифровое небо» (патент № 20266181823) прямо называет
 * «воздушные коридоры/зоны (3D-расчёт)», поэтому плоская карта — прямой
 * функциональный проигрыш.
 *
 * СТАЛО: те же полигоны объёмные — MapLibre fill-extrusion поднимает их от
 * lower_limit до upper_limit. Плюс «шляпка» верхней кромки (fill-extrusion с
 * непрозрачной заливкой и нулевой высотой на upper_limit), чтобы верхняя
 * граница полосы высот читалась глазом, а не угадывалась.
 *
 * ⚠️ КРИТИЧНО, единицы измерения. fill-extrusion-base/height в MapLibre —
 * это МЕТРЫ над уровнем моря, а не футы и не «высота над рельефом».
 * Наши ASL-значения из /api/geo — метры над MSL, поэтому кладутся напрямую.
 * Если бы в выгрузке были футы, объём уехал бы в 3,3 раза выше.
 * ======================================================================== */

/** Привести к конечному числу; строки с бэкенда («120.00») тоже берём */
const alt = (v: unknown, fallback = 0): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

/** Порядок слоёв 3D: сначала объём (низ), поверх — верхняя кромка */
export const LAYERS_3D: FillExtrusionLayerSpecification[] = [
  // --- Объёмные геозоны: от нижней границы до верхней ---
  {
    id: 'geofences-extrusion',
    type: 'fill-extrusion',
    source: SRC.geofences,
    paint: {
      'fill-extrusion-color': LAYER_COLOR.geofences,
      // 0.35 — чтобы просвечивали цели внутри объёма; сплошной блок нечитаем
      'fill-extrusion-opacity': 0.35,
      'fill-extrusion-base': ['coalesce', ['get', 'lowerLimit'], 0],
      // bandHeight = толщина полосы в м (для AGL-зон base уже ASL-низ рельефа+lower)
      'fill-extrusion-height': ['max', ['coalesce', ['get', 'bandHeight'], 0], 1],
      // Подсветка верхней кромки: она и есть признак «зона высот»
      'fill-extrusion-vertical-gradient': true,
    },
  },

  // --- Верхняя кромка: тонкая «шапка» на upper_limit ---
  {
    id: 'geofences-cap',
    type: 'fill-extrusion',
    source: SRC.geofences,
    paint: {
      'fill-extrusion-color': LAYER_COLOR.geofences,
      'fill-extrusion-opacity': 0.9,
      // База = верхняя граница, высота ~1 м -> тонкая горизонтальная площадка
      'fill-extrusion-base': ['coalesce', ['get', 'upperLimit'], 0],
      'fill-extrusion-height': 1,
      'fill-extrusion-vertical-gradient': false,
    },
  },

  // --- Объёмные районы заданий ---
  {
    id: 'missions-extrusion',
    type: 'fill-extrusion',
    source: SRC.missions,
    paint: {
      'fill-extrusion-color': LAYER_COLOR.missions,
      'fill-extrusion-opacity': 0.3,
      'fill-extrusion-base': ['coalesce', ['get', 'lowerLimit'], 0],
      'fill-extrusion-height': ['max', ['coalesce', ['get', 'bandHeight'], ['-', ['coalesce', ['get', 'upperLimit'], 0], ['coalesce', ['get', 'lowerLimit'], 0]]], 1],
      'fill-extrusion-vertical-gradient': true,
    },
  },
  {
    id: 'missions-cap',
    type: 'fill-extrusion',
    source: SRC.missions,
    paint: {
      'fill-extrusion-color': LAYER_COLOR.missions,
      'fill-extrusion-opacity': 0.85,
      'fill-extrusion-base': ['coalesce', ['get', 'upperLimit'], 0],
      'fill-extrusion-height': 1,
      'fill-extrusion-vertical-gradient': false,
    },
  },
]

/** Источники карты */
export const SOURCES: Record<string, GeoJSONSourceSpecification> = {
  [SRC.missions]: { type: 'geojson', data: emptyCollection() },
  [SRC.geofences]: { type: 'geojson', data: emptyCollection() },
  [SRC.declarations]: { type: 'geojson', data: emptyCollection() },
  [SRC.cooperative]: { type: 'geojson', data: emptyCollection() },
  [SRC.nonCooperative]: { type: 'geojson', data: emptyCollection() },
  [SRC.intruders]: { type: 'geojson', data: emptyCollection() },
  [SRC.vessels]: { type: 'geojson', data: emptyCollection() },
  [SRC.trails]: { type: 'geojson', data: emptyCollection() },
  [SRC.predict10]: { type: 'geojson', data: emptyCollection() },
  [SRC.predict60]: { type: 'geojson', data: emptyCollection() },
  [SRC.overlayReal]: { type: 'geojson', data: emptyCollection() },
  [SRC.overlayTwin]: { type: 'geojson', data: emptyCollection() },
}

/**
 * Слои стиля. Порядок отрисовки снизу вверх:
 * полигоны → линии → точки (чтобы маркеры не перекрывались заливкой).
 */
export const LAYERS: Array<
  FillLayerSpecification | LineLayerSpecification | CircleLayerSpecification
> = [
  // --- 1. Районы выполнения заданий (заливка + контур) ---
  {
    id: 'missions-fill',
    type: 'fill',
    source: SRC.missions,
    paint: { 'fill-color': LAYER_COLOR.missions, 'fill-opacity': 0.12 },
  },
  {
    id: 'missions-outline',
    type: 'line',
    source: SRC.missions,
    paint: { 'line-color': LAYER_COLOR.missions, 'line-width': 2 },
  },

  // --- 2. Геозоны ---
  {
    id: 'geofences-fill',
    type: 'fill',
    source: SRC.geofences,
    paint: { 'fill-color': LAYER_COLOR.geofences, 'fill-opacity': 0.08 },
  },
  {
    id: 'geofences-outline',
    type: 'line',
    source: SRC.geofences,
    paint: {
      'line-color': LAYER_COLOR.geofences,
      'line-width': 2,
      'line-dasharray': [4, 3],
    },
  },

  // --- 3. Декларации и маршруты ---
  {
    id: 'declarations-fill',
    type: 'fill',
    source: SRC.declarations,
    paint: { 'fill-color': LAYER_COLOR.declarations, 'fill-opacity': 0.1 },
  },
  {
    id: 'declarations-outline',
    type: 'line',
    source: SRC.declarations,
    paint: { 'line-color': LAYER_COLOR.declarations, 'line-width': 2 },
  },

  // --- 3b. Хвосты траекторий (FR24-стиль, 2026-10-07) ---
  {
    id: 'trails-lines',
    type: 'line',
    source: SRC.trails,
    paint: {
      'line-color': LAYER_COLOR.trails,
      'line-width': 2,
      'line-opacity': 0.55,
    },
  },
  // --- 3c. Прогнозные дуги: 10с сплошная яркая, 60с пунктир приглушённый ---
  // Подсветка конфликтов (2026-10-07f): свойство conflict=1 ставит App.tsx
  // из /api/trajectories.conflicts[horizon] (пара ближе ZG_PRED_SEP_M=500 м
  // на горизонте). Дуга борта-участника пары рисуется цветом нарушителей —
  // «куда столкнутся» читается тем же кодом опасности. Data-выражение, а не
  // два отдельных слоя: свойства приходят заново с каждым поллингом (4 с),
  // у неконфликтного борта свойства conflict просто нет -> case даёт дефолт.
  {
    id: 'predict10-lines',
    type: 'line',
    source: SRC.predict10,
    paint: {
      'line-color': [
        'case',
        ['==', ['get', 'conflict'], 1],
        LAYER_COLOR.predictConflict,
        LAYER_COLOR.predict,
      ],
      'line-width': ['case', ['==', ['get', 'conflict'], 1], 4, 3],
      'line-opacity': 0.9,
    },
  },
  {
    id: 'predict60-lines',
    type: 'line',
    source: SRC.predict60,
    paint: {
      'line-color': [
        'case',
        ['==', ['get', 'conflict'], 1],
        LAYER_COLOR.predictConflict,
        LAYER_COLOR.predict,
      ],
      'line-width': 2,
      // Конфликтная дуга пунктиром чуть ярче, чтобы читалась на фоне маршрута
      'line-opacity': ['case', ['==', ['get', 'conflict'], 1], 0.8, 0.45],
      'line-dasharray': [2, 2],
    },
  },
  // --- Оверлей двойника (D3): «двойник vs реальность» ---
  // reality-хвост (фактическая траектория борта) и twin-прогноз (пунктир) —
  // накладываются ПОВЕРХ predicts, чтобы диспетчер сравнивал глазами.
  {
    id: 'overlay-real-lines',
    type: 'line',
    source: SRC.overlayReal,
    paint: {
      'line-color': LAYER_COLOR.overlayReal,
      'line-width': 2.5,
      'line-opacity': 0.55,
    },
  },
  {
    id: 'overlay-twin-lines',
    type: 'line',
    source: SRC.overlayTwin,
    paint: {
      'line-color': LAYER_COLOR.overlayTwin,
      'line-width': 2,
      'line-opacity': 0.9,
      'line-dasharray': [3, 3],
    },
  },

  // --- 4. Кооперативные цели (ADS-B / Remote ID) ---
  {
    id: 'cooperative-tracks',
    type: 'circle',
    source: SRC.cooperative,
    paint: {
      'circle-radius': 7,
      'circle-color': LAYER_COLOR.cooperative,
      'circle-opacity': 0.85,
      'circle-stroke-color': '#0f172a',
      'circle-stroke-width': 1.5,
    },
  },

  // --- 5. Некооперативные цели (РЛС / RF) — пунктир для отличия ---
  {
    id: 'noncooperative-tracks',
    type: 'circle',
    source: SRC.nonCooperative,
    // Некооперативная цель: полая заливка с кольцом — визуально отличается от кооперативной
    paint: {
      'circle-radius': 7,
      'circle-color': LAYER_COLOR.nonCooperative,
      'circle-opacity': 0.25,
      'circle-stroke-color': LAYER_COLOR.nonCooperative,
      'circle-stroke-width': 2,
    },
  },

  // --- 6. НАРУШИТЕЛИ: красные, крупные, пульсирующие ---
  {
    id: 'intruders-halo',
    type: 'circle',
    source: SRC.intruders,
    paint: {
      'circle-radius': 14,
      'circle-color': LAYER_COLOR.intruders,
      'circle-opacity': 0.18,
      'circle-stroke-color': LAYER_COLOR.intruders,
      'circle-stroke-width': 1,
      'circle-stroke-opacity': 0.6,
    },
  },
  {
    id: 'intruders',
    type: 'circle',
    source: SRC.intruders,
    paint: {
      'circle-radius': 9,
      'circle-color': LAYER_COLOR.intruders,
      'circle-opacity': 1,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 2,
    },
  },

  // --- Суда в зоне (для оценки пересечения доменов) ---
  {
    id: 'vessels',
    type: 'circle',
    source: SRC.vessels,
    paint: {
      'circle-radius': 6,
      'circle-color': LAYER_COLOR.vessels,
      'circle-opacity': 0.8,
      'circle-stroke-color': '#0f172a',
      'circle-stroke-width': 1.5,
    },
  },
]

/* ============================================================================
 * ПРЕОБРАЗОВАНИЕ ДАННЫХ В GeoJSON
 * ========================================================================= */

/** Наблюдение трафика → Feature<Point> с нормализованными полями */
export function trafficToFeature(obs: TrafficObservation, index: number): Feature<Point> {
  // ⚠️ Консоль /api/traffic отдаёт формат FB: lat_dd/lon_dd (проверено 2026-10-06:
  // в ответе есть только lat_dd/lon_dd, полей lat/lon нет). Читаем ОБА формата —
  // иначе Number(undefined)=NaN и цели молча улетают в (0,0) у берегов Африки.
  const lat = Number(obs.lat ?? (obs as unknown as { lat_dd?: number }).lat_dd ?? 0)
  const lon = Number(obs.lon ?? (obs as unknown as { lon_dd?: number }).lon_dd ?? 0)
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lon, lat] },
    properties: {
      id: String(obs.id ?? obs.icao ?? obs.callsign ?? `traffic-${index}`),
      callsign: obs.callsign ?? obs.icao ?? 'БЕЗ ОПОЗНАНИЯ',
      alt_m: Number(obs.alt_m ?? 0),
      speed_ms: Number(obs.speed_ms ?? 0),
      heading_deg: Number(obs.heading_deg ?? 0),
      cooperative: obs.cooperative !== false,
      source: obs.source ?? 'ADS-B',
      observed_at: obs.observed_at ?? '',
    },
  }
}

/** Разбивает наблюдения на кооперативные / некооперативные / нарушители.
 *  Нарушителем считается цель, не имеющая идентификации внутри контролируемой зоны. */
export function splitTraffic(observations: TrafficObservation[]): {
  cooperative: Feature<Point>[]
  nonCooperative: Feature<Point>[]
  intruders: Feature<Point>[]
} {
  const cooperative: Feature<Point>[] = []
  const nonCooperative: Feature<Point>[] = []
  const intruders: Feature<Point>[] = []

  observations.forEach((obs, i) => {
    const f = trafficToFeature(obs, i)
    const hasId = Boolean(obs.icao || obs.callsign)
    // Цель без позывного/ICAO и вне контролируемой зоны — потенциальный нарушитель
    const isIntruder = !hasId && obs.cooperative === false
    if (isIntruder) intruders.push(f)
    else if (obs.cooperative === false) nonCooperative.push(f)
    else cooperative.push(f)
  })

  return {
    cooperative,
    nonCooperative,
    intruders,
  }
}

/** Геозон → Feature<Polygon> */
export function geofenceToFeature(fence: Geofence, index: number): Feature<Polygon> | null {
  const poly = fence.polygon
  if (!Array.isArray(poly) || poly.length < 3) return null
  // ⚠️ КРИТИЧНО (найдено 2026-10-06, причина «геозоны нет на карте»):
  // /api/geo.polygon отдаёт [[lat, lon], ...] (App.tsx кладёт как есть),
  // а GeoJSON требует [lon, lat]! Раньше ring шёл в coordinates КАК ЕСТЬ —
  // широта в роли долготы, MapLibre молча отбрасывал невалидную геометрию
  // (lat 55 > 90 невозможен → за пределами мира). Разворачиваем пары.
  const ring: Array<[number, number]> = poly.map(([a, b]) => {
    // эвристика: широта по модулю <=90, долгота <=180; наши данные всегда
    // [lat, lon], но защищаемся от уже-правильного входа
    const looksLatFirst = Math.abs(a) <= 90 && Math.abs(b) <= 180 && Math.abs(a) > Math.abs(b) ? true : (Math.abs(b) <= 90 && Math.abs(a) <= 180 && Math.abs(b) > Math.abs(a) ? false : true)
    return looksLatFirst ? [b, a] as [number, number] : [a, b] as [number, number]
  })
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first)

  // Полоса высот в метрах над MSL. Именно эти два поля решают, будет зона
  // плоской заливкой или объёмом: их читает fill-extrusion-base/height.
  // upper < lower возможен в «битых» данных — тогда показываем 1 м, но НЕ
  // молча меняем местами границы: это изменило бы смысл ограничения.
  const ref = Number(fence.altitude_ref ?? 0)   // 0=AMSL, 1=AGL (InterUSS)
  const lowerLimit = alt(fence.lower_limit, 0)
  const upperLimit = Math.max(alt(fence.upper_limit, 0), lowerLimit)

  /**
   * AGSL (altitude_ref=1): limits — метры НАД РЕЛЬЕФОМ. MapLibre fill-extrusion
   * не умеет heightReference (это Cesium), поэтому переводим в ASL на фронте:
   * base = ground_min (минимальный рельеф под зоной, бэкенд считает по DEM),
   * height = толщина полосы = upper - lower. Так нижняя кромка лежит на самом
   * низком месте рельефа и объём не зарезается холмами (проверено на Москве:
   * рельеф под зоной 125.6..150.7 м, 0 зарезанных вершин).
   * Честность: ground_min === null -> объём НЕ рисуем (нет данных), показываем
   * плоскую заливку + подпись «AGL»; подстановка 0 запрещена — это ложь «на земле».
   */
  const groundMin = fence.ground_min
  const isAgl = ref === 1
  const hasGround = isAgl && typeof groundMin === 'number' && Number.isFinite(groundMin)

  const baseAsl = hasGround ? (groundMin as number) + lowerLimit : lowerLimit
  const heightM = Math.max(upperLimit - lowerLimit, 1)

  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring] },
    properties: {
      id: String(fence.id ?? `geofence-${index}`),
      name: fence.name ?? `Геозона ${index + 1}`,
      kind: fence.kind ?? 'ограничение',
      /** base для fill-extrusion: уже в ASL-метрах карты */
      lowerLimit: baseAsl,
      /** толщина объёма (AGL-полоса), метры */
      bandHeight: heightM,
      /** верхняя кромка в ASL для «шляпки» */
      upperLimit: baseAsl + heightM,
      /** признак AGL-зоны: подпись и стили */
      altitudeRef: ref,
      agl: isAgl ? 1 : 0,
      aglNoGround: isAgl && !hasGround ? 1 : 0,
      /** Для подписи на карте: «0–120 м AGL» / «0–120 м» */
      altLabel: `${Math.round(lowerLimit)}–${Math.round(upperLimit)} м${isAgl ? ' AGL' : ''}`,
      /** Источник зоны (п.2, 2026-10-06): mintc | sppi | own */
      source: (fence.source ?? 'own') as string,
    },
  }
}

/** Маршрут декларации → Feature<LineString> */
export function routeToFeature(
  route: Array<[number, number]>,
  props: Record<string, unknown> = {},
): Feature<LineString> | null {
  if (!Array.isArray(route) || route.length < 2) return null
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: route },
    properties: props,
  }
}

/**
 * Хвост траектории (FR24) → Feature<LineString>.
 * points: [[ts, lat, lon, alt], ...] как отдаёт /api/trajectories.trails.
 */
export function trailToFeature(
  points: Array<[number, number, number, number]>,
  props: Record<string, unknown> = {},
): Feature<LineString> | null {
  if (!Array.isArray(points) || points.length < 2) return null
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: points.map(([, lat, lon]) => [lon, lat] as [number, number]),
    },
    properties: props,
  }
}

/**
 * Прогнозная дуга → Feature<LineString>.
 * path: [[lat, lon], ...] как отдаёт /api/trajectories.predict[h].path.
 */
export function predictToFeature(
  path: Array<[number, number]>,
  props: Record<string, unknown> = {},
): Feature<LineString> | null {
  if (!Array.isArray(path) || path.length < 2) return null
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: path.map(([lat, lon]) => [lon, lat] as [number, number]),
    },
    properties: props,
  }
}

/** Судно → Feature<Point> */
export function vesselToFeature(vessel: Vessel, index: number): Feature<Point> {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [Number(vessel.lon ?? 0), Number(vessel.lat ?? 0)],
    },
    properties: {
      id: String(vessel.id ?? `vessel-${index}`),
      name: vessel.name ?? vessel.callsign ?? 'Судно',
      type: vessel.type ?? 'неизвестно',
      alt_m: Number(vessel.alt_m ?? 0),
    },
  }
}

/** Собирает FeatureCollection */
export function toCollection(features: Feature[]): FeatureCollection {
  return { type: 'FeatureCollection', features }
}

/** Оверлей двойника (D3): reality-хвосты + twin-прогноз в GeoJSON-линии.
 * twin_overlay.py отдаёт контракт /api/trajectories, поэтому trails/predict
 * переиспользуем как есть (trailToFeature/predictToFeature). Возврат двух
 * наборов — [realityFeatures, twinFeatures]. */
export function twinOverlayToFeatures(
  ov: TwinOverlay | null | undefined,
): { reality: Feature<LineString>[]; twin: Feature<LineString>[] } {
  const reality: Feature<LineString>[] = []
  const twin: Feature<LineString>[] = []
  if (!ov?.ok) return { reality, twin }
  for (const t of ov.trails ?? []) {
    const f = trailToFeature(t.points, { drone: t.drone })
    if (f) reality.push(f)
  }
  for (const p of ov.predict ?? []) {
    // двойник-прогноз: берём 10-секундную дугу (метод linear) как «куда
    // двойник ожидает борт»; конфликтную красим свойством для карточки.
    const pr = p.predict?.['10']
    if (pr?.path && pr.path.length >= 2) {
      const f = predictToFeature(pr.path, { drone: p.drone })
      if (f) twin.push(f)
    }
  }
  return { reality, twin }
}
